import React, { ReactNode, useEffect, useState } from 'react';
import { Box, Grid, Stack, Typography, useMediaQuery } from '@mui/material';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import { CheckOutlined, RobotOutlined, SafetyCertificateOutlined, AppstoreOutlined } from '@ant-design/icons';

import Button from '../../components/ui/Button';
import { MCP_PANEL_SX } from '../../utils/mcp';

// ============================================================================
// Signal Desk — "mission control" visual language for the MCP hub.
// The spec is dark-mode-first; everything here is mapped to MUI theme tokens
// (palette + alpha) so it renders correctly in BOTH light and dark themes.
// Brand teal (palette.primary) is the "armed/live" signal colour throughout.
// ============================================================================

// ink() picks a readable tint for small colored TEXT: the theme darkens .main in
// dark mode (teal ≈ 3.2:1 on the panel bg), so 11-13px labels use .light there.
// Dots, bars, and borders keep .main — this is for type only.
export const ink = (theme: Theme, key: 'primary' | 'error' | 'warning' | 'success' = 'primary') =>
  theme.palette.mode === 'dark' ? theme.palette[key].light : theme.palette[key].main;

// The two micro-label tiers. labelSx = instrument labels (Eyebrow); statusSx =
// state words (ARMED / STANDBY / POLICY GATE) — one tracking, everywhere.
export const labelSx = { fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' } as const;
export const statusSx = { fontSize: 11, fontWeight: 700, letterSpacing: '0.12em' } as const;

// Shared page-title spec — the tier above panel titles (18px), so each surface
// has a typographic entry point that outranks its cards.
export const pageTitleSx = {
  fontSize: { xs: 22, sm: 26 },
  fontWeight: 700,
  letterSpacing: '-0.02em',
  lineHeight: 1.2
} as const;

// Staggered entrance for page sections — "designed, not assembled".
export const riseIn = (i: number) => ({
  '@keyframes mcpRise': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } },
  animation: `mcpRise .4s ease ${i * 70}ms backwards`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
});

// A faint teal radial wash at the top of a surface — atmosphere, not decoration.
// Sits behind the header/schematic so the canvas reads "powered on", both themes.
export const atmosphereSx = (theme: Theme) => ({
  backgroundImage: `radial-gradient(900px 340px at 18% -60px, ${alpha(
    theme.palette.primary.main,
    theme.palette.mode === 'dark' ? 0.07 : 0.05
  )}, transparent 70%)`,
  backgroundRepeat: 'no-repeat'
});

// Count-up for instrument numerals: eases 0 → value on mount (~700ms). Falls
// back to a static value for non-numeric strings and reduced-motion users.
export const CountUpValue = ({ value, duration = 700 }: { value: string; duration?: number }) => {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const target = Number(value);
  const animatable = Number.isFinite(target) && target > 0 && !reduceMotion;
  const [display, setDisplay] = useState(animatable ? 0 : target);

  useEffect(() => {
    if (!animatable) {
      setDisplay(target);
      return undefined;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, animatable]);

  return <>{Number.isFinite(target) ? String(display) : value}</>;
};

// Tiny activity sparkline for instrument tiles. Renders nothing when there is
// no signal (all zeros) — an empty chart would just be noise.
export const Sparkline = ({
  points,
  color,
  width = 64,
  height = 18
}: {
  points: number[];
  color: string;
  width?: number;
  height?: number;
}) => {
  if (!points.length || points.every((p) => p === 0)) return null;
  const max = Math.max(...points, 1);
  const step = points.length > 1 ? width / (points.length - 1) : width;
  const coords = points.map((p, i) => [i * step, height - 2 - (p / max) * (height - 4)] as const);
  const last = coords[coords.length - 1];
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${width} ${height}`}
      sx={{ width, height, display: 'block', overflow: 'visible', flexShrink: 0 }}
      aria-hidden
    >
      <polyline
        points={coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.85}
      />
      <circle cx={last[0].toFixed(1)} cy={last[1].toFixed(1)} r={2} fill={color} />
    </Box>
  );
};

// instrument micro-label: 11px uppercase, tracked out, muted.
export const Eyebrow = ({ children, sx }: { children: ReactNode; sx?: object }) => (
  <Typography sx={{ ...labelSx, color: 'text.secondary', ...sx }}>{children}</Typography>
);

const panelBg = (theme: Theme) => (theme.palette.mode === 'dark' ? alpha('#fff', 0.015) : alpha(theme.palette.text.primary, 0.015));
const insetTop = (theme: Theme) =>
  theme.palette.mode === 'dark' ? 'inset 0 1px 0 rgba(255,255,255,0.03)' : 'inset 0 1px 0 rgba(255,255,255,0.6)';

// ---- Midnight Desk console surface -------------------------------------------

// Fixed inks for the dashboard's console panel — the same midnight family as
// the locked landing's banner and the setup boot readout, deliberately NOT
// theme-driven: the desk is dark in both themes because the product IS a dark
// terminal. Keep in step with McpIntro's STAGE and McpHubPage's BOOT_INK.
export const DESK = {
  bg: '#0B1416',
  panel: 'rgba(255,255,255,0.06)',
  line: 'rgba(148,196,192,0.28)',
  // the fine blueprint grid stays quieter than structural lines
  grid: 'rgba(148,196,192,0.14)',
  text: '#E9F4F2',
  dim: 'rgba(210,232,229,0.74)',
  faint: 'rgba(210,232,229,0.52)'
} as const;

// The console's outer surface: midnight base, teal glows, blueprint grid —
// panel-scale sibling of the landing banner's backdrop.
export const midnightConsoleSx = (theme: Theme) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
  bgcolor: DESK.bg,
  color: DESK.text,
  boxShadow: `0 24px 64px -28px ${alpha('#000', 0.5)}, inset 0 1px 0 rgba(255,255,255,0.06)`,
  backgroundImage: [
    `radial-gradient(640px 260px at 10% -60px, ${alpha(theme.palette.primary.main, 0.2)}, transparent 65%)`,
    `radial-gradient(520px 240px at 95% 115%, ${alpha(theme.palette.primary.main, 0.12)}, transparent 70%)`,
    `linear-gradient(${DESK.grid} 1px, transparent 1px)`,
    `linear-gradient(90deg, ${DESK.grid} 1px, transparent 1px)`
  ].join(','),
  backgroundSize: 'auto, auto, 44px 44px, 44px 44px',
  backgroundPosition: '0 0, 0 0, -1px -1px, -1px -1px'
});

// Breathing status dot — teal when armed, dim otherwise.
export const StatusDot = ({ armed, size = 8, midnight }: { armed?: boolean; size?: number; midnight?: boolean }) => (
  <Box
    aria-hidden
    sx={(theme) => ({
      width: size,
      height: size,
      borderRadius: '50%',
      flexShrink: 0,
      // on the midnight console, light-theme text.primary would be dark-on-dark
      bgcolor: armed ? theme.palette.primary.main : midnight ? 'rgba(233,244,242,0.3)' : alpha(theme.palette.text.primary, 0.25),
      boxShadow: armed ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}` : 'none',
      transition: 'background-color .25s ease, box-shadow .25s ease',
      '@keyframes mcpBreathe': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
      animation: armed ? 'mcpBreathe 2.4s ease-in-out infinite' : 'none',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' }
    })}
  />
);

// A thin meter showing the per-trade lot ceiling (full + amber = no limit).
export const LotMeter = ({ maxLot, width = 72 }: { maxLot: number; width?: number }) => {
  const noLimit = !maxLot || maxLot <= 0;
  const pct = noLimit ? 100 : Math.min(maxLot / 2, 1) * 100;
  return (
    <Box
      sx={(theme) => ({
        mt: 0.5,
        width,
        height: 3,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.text.primary, 0.12),
        overflow: 'hidden'
      })}
    >
      <Box
        sx={(theme) => ({
          height: '100%',
          width: `${pct}%`,
          borderRadius: 2,
          bgcolor: noLimit ? theme.palette.warning.main : theme.palette.primary.main,
          transition: 'width .35s cubic-bezier(.4,0,.2,1), background-color .35s ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
        })}
      />
    </Box>
  );
};

// Numbered step circle shared by the connect panel (24px, idle) and onboarding
// (32px, done/active/future).
export const StepBadge = ({
  n,
  size = 24,
  state = 'idle'
}: {
  n: number;
  size?: number;
  state?: 'done' | 'active' | 'future' | 'idle';
}) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size / 2 - 2,
        fontWeight: 600,
        ...(state === 'done' && { color: theme.palette.primary.contrastText, bgcolor: primary }),
        ...(state === 'active' && {
          color: ink(theme),
          bgcolor: alpha(primary, 0.12),
          border: `2px solid ${primary}`,
          boxShadow: `0 0 0 4px ${alpha(primary, 0.12)}`
        }),
        ...(state === 'idle' && { color: ink(theme), border: `2px solid ${alpha(primary, 0.7)}` }),
        ...(state === 'future' && { color: 'text.disabled', border: `1.5px solid ${theme.palette.divider}` })
      }}
    >
      {state === 'done' ? <CheckOutlined /> : n}
    </Box>
  );
};

// ---- the signature: AI ⇄ POLICY GATE ⇄ accounts schematic --------------------

const SchematicNode = ({
  icon,
  title,
  lines,
  emphasised,
  live,
  midnight
}: {
  icon: ReactNode;
  title: string;
  lines: string[];
  emphasised?: boolean;
  live?: boolean;
  midnight?: boolean;
}) => (
  <Box
    sx={(theme) => ({
      flex: 1,
      minWidth: 200,
      p: 2,
      borderRadius: '12px',
      bgcolor: midnight ? DESK.panel : panelBg(theme),
      border: `1px solid ${
        emphasised ? alpha(theme.palette.primary.main, live ? 0.6 : 0.4) : midnight ? DESK.line : theme.palette.divider
      }`,
      boxShadow: (() => {
        // light-theme insetTop is a 0.6-alpha white line — far too harsh on the
        // midnight surface; the console gets its own subtle top catch-light.
        const inset = midnight ? 'inset 0 1px 0 rgba(255,255,255,0.07)' : insetTop(theme);
        return emphasised && live ? `${inset}, 0 0 26px -8px ${alpha(theme.palette.primary.main, 0.6)}` : inset;
      })(),
      transition: 'border-color .25s ease, box-shadow .25s ease',
      position: 'relative'
    })}
  >
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
      <Box
        sx={(theme) => ({
          width: 30,
          height: 30,
          borderRadius: '9px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 15,
          color: midnight
            ? emphasised
              ? theme.palette.primary.light
              : DESK.dim
            : emphasised
            ? ink(theme)
            : theme.palette.text.secondary,
          bgcolor: midnight
            ? alpha(emphasised ? theme.palette.primary.main : '#ffffff', emphasised ? 0.16 : 0.06)
            : alpha(emphasised ? theme.palette.primary.main : theme.palette.text.primary, 0.1)
        })}
      >
        {icon}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: midnight ? DESK.text : 'text.primary' }}>
        {title}
      </Typography>
    </Stack>
    {lines.map((l, i) => (
      <Typography key={i} variant="caption" sx={{ display: 'block', lineHeight: 1.5, color: midnight ? DESK.dim : 'text.secondary' }}>
        {l}
      </Typography>
    ))}
  </Box>
);

// hairline wire (md+ only); the teal pulse ferries along it only when active.
const Wire = ({ delay = 0, active = true, midnight }: { delay?: number; active?: boolean; midnight?: boolean }) => (
  <Box
    aria-hidden
    sx={(theme) => ({
      display: { xs: 'none', md: 'block' },
      position: 'relative',
      flex: '0 0 46px',
      height: 2,
      alignSelf: 'center',
      borderRadius: 2,
      background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, midnight ? (active ? 0.55 : 0.25) : active ? 0.35 : 0.15)}, ${
        midnight ? DESK.line : theme.palette.divider
      })`,
      '&::after': {
        content: '""',
        position: 'absolute',
        top: -2,
        width: 6,
        height: 6,
        borderRadius: '50%',
        bgcolor: theme.palette.primary.main,
        boxShadow: `0 0 8px 1px ${alpha(theme.palette.primary.main, 0.7)}`,
        '@keyframes mcpWireFlow': {
          '0%': { left: '-4px', opacity: 0 },
          '15%': { opacity: 1 },
          '85%': { opacity: 1 },
          '100%': { left: 'calc(100% - 2px)', opacity: 0 }
        },
        animation: active ? `mcpWireFlow 2.4s ${delay}s linear infinite` : 'none',
        display: active ? 'block' : 'none'
      },
      // MUST come after '&::after' — emotion emits rules in object order, so a
      // media query declared first is overridden by the base rule and the
      // reduced-motion preference never actually stops the pulse.
      '@media (prefers-reduced-motion: reduce)': { '&::after': { animation: 'none', display: 'none' } }
    })}
  />
);

// bare renders the schematic without its own panel chrome, for composing into
// the Dashboard's console panel (schematic + instrument strip as one object).
export const ConnectionSchematic = ({
  armedCount,
  total,
  bare,
  midnight
}: {
  armedCount: number;
  total: number;
  bare?: boolean;
  midnight?: boolean;
}) => {
  // Glow + execution pulse only when something is actually armed — motion must
  // not lie about system state. The AI→gate wire stays live (the connection is).
  const live = armedCount > 0;
  return (
    <Box sx={{ ...(bare ? {} : MCP_PANEL_SX), p: { xs: 2, sm: 2.75 } }}>
      <Eyebrow sx={{ mb: 2, ...(midnight && { color: DESK.faint }) }}>Connection schematic · {live ? 'live' : 'standby'}</Eyebrow>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 3, md: 0 }} alignItems="stretch">
        <SchematicNode midnight={midnight} icon={<RobotOutlined />} title="AI assistant" lines={['Claude / ChatGPT', 'via MCP protocol']} />
        <Wire delay={0} midnight={midnight} />
        <Box sx={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: -9,
              left: 16,
              zIndex: 1,
              px: 1,
              py: '2px',
              borderRadius: '6px',
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.12),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`
            })}
          >
            <Typography sx={{ ...statusSx, color: (theme) => (midnight ? theme.palette.primary.light : ink(theme)) }}>
              POLICY GATE
            </Typography>
          </Box>
          <SchematicNode
            midnight={midnight}
            icon={<SafetyCertificateOutlined />}
            title="Traders Connect guardrails"
            lines={['Per-account arming · max lot per trade', 'allowed & blocked symbols']}
            emphasised
            live={live}
          />
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
            {['Lot cap', 'Symbol allowlist', 'Arm switch'].map((t) => (
              <Box
                key={t}
                sx={(theme) => ({
                  px: 1,
                  py: '2px',
                  borderRadius: '6px',
                  fontSize: 11,
                  color: midnight ? DESK.dim : 'text.secondary',
                  border: `1px solid ${midnight ? DESK.line : theme.palette.divider}`
                })}
              >
                {t}
              </Box>
            ))}
          </Stack>
        </Box>
        <Wire delay={0.6} active={live} midnight={midnight} />
        <SchematicNode
          midnight={midnight}
          icon={<AppstoreOutlined />}
          title="Your accounts"
          lines={[`${total} connected`, live ? `${armedCount} armed · ${Math.max(total - armedCount, 0)} standby` : 'all on standby']}
        />
      </Stack>
      {/* the trust line from the rework wireframe, in the app's own voice.
          text.secondary, not text.disabled — this is the product's core trust
          claim and must clear WCAG contrast, not whisper at ~2.5:1. */}
      <Typography variant="caption" sx={{ display: 'block', mt: 2, fontWeight: 400, color: midnight ? DESK.dim : 'text.secondary' }}>
        The gate runs on Traders Connect servers — the AI can’t read, edit, or bypass it. A limit changed here applies to the very next
        proposed order.
      </Typography>
    </Box>
  );
};

// ---- instrument stat band ----------------------------------------------------

export type Instrument = {
  label: string;
  value: string;
  sub: string;
  tone?: 'default' | 'teal' | 'amber' | 'success';
  armed?: boolean;
  spark?: number[];
};

// bare renders the tiles without their own panel chrome (see ConnectionSchematic).
export const InstrumentBand = ({ items, bare, midnight }: { items: Instrument[]; bare?: boolean; midnight?: boolean }) => {
  const theme = useTheme();
  // On the midnight console the theme's .main tints are too dark — use .light.
  const toneColor = (t?: string) =>
    t === 'teal'
      ? midnight
        ? theme.palette.primary.light
        : ink(theme)
      : t === 'amber'
      ? midnight
        ? theme.palette.warning.light
        : ink(theme, 'warning')
      : t === 'success'
      ? midnight
        ? theme.palette.success.light
        : ink(theme, 'success')
      : midnight
      ? DESK.text
      : theme.palette.text.primary;
  const tileLine = midnight ? DESK.line : theme.palette.divider;
  return (
    <Box sx={{ ...(bare ? {} : MCP_PANEL_SX), overflow: 'hidden' }}>
      <Grid container>
        {items.map((it, i) => (
          <Grid
            key={it.label}
            item
            xs={6}
            sm={3}
            sx={{
              p: { xs: 2, sm: 2.75 },
              borderLeft: {
                xs: i % 2 === 1 ? `1px solid ${tileLine}` : 'none',
                sm: i % 4 === 0 ? 'none' : `1px solid ${tileLine}`
              },
              borderTop: { xs: i >= 2 ? `1px solid ${tileLine}` : 'none', sm: 'none' }
            }}
          >
            <Eyebrow sx={{ mb: 0.75, ...(midnight && { color: DESK.faint }) }}>{it.label}</Eyebrow>
            <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={1}>
              <Typography
                sx={{
                  fontSize: 34,
                  fontWeight: 600,
                  lineHeight: 1,
                  letterSpacing: '-0.01em',
                  fontVariantNumeric: 'tabular-nums',
                  color: toneColor(it.tone),
                  transition: 'color .25s ease'
                }}
              >
                <CountUpValue value={it.value} />
              </Typography>
              {it.spark && (
                <Sparkline
                  points={it.spark}
                  color={
                    it.tone === 'amber'
                      ? theme.palette.warning.main
                      : it.tone === 'success'
                      ? theme.palette.success.main
                      : theme.palette.primary.main
                  }
                />
              )}
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
              {it.armed !== undefined && <StatusDot armed={it.armed} size={6} midnight={midnight} />}
              <Typography variant="caption" noWrap sx={{ fontVariantNumeric: 'tabular-nums', color: midnight ? DESK.dim : 'text.secondary' }}>
                {it.sub}
              </Typography>
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ---- mission-control header --------------------------------------------------

// SYSTEM ARMED / STANDBY status pill — teal + breathing dot when armed.
export const SystemPill = ({ armed, label }: { armed: boolean; label?: string }) => (
  <Box
    sx={(theme) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      pl: 1.5,
      pr: 1.75,
      py: 0.75,
      borderRadius: '999px',
      whiteSpace: 'nowrap',
      border: `1px solid ${armed ? alpha(theme.palette.primary.main, 0.45) : theme.palette.divider}`,
      bgcolor: armed ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08) : 'transparent',
      transition: 'background-color .25s ease, border-color .25s ease',
      '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
    })}
  >
    <StatusDot armed={armed} />
    <Typography
      sx={{
        ...statusSx,
        color: (theme: Theme) => (armed ? ink(theme) : theme.palette.text.secondary),
        transition: 'color .25s ease'
      }}
    >
      {label || (armed ? 'SYSTEM ARMED' : 'SYSTEM STANDBY')}
    </Typography>
  </Box>
);

// Plain page header — title + subtitle + setup entry, matching the rest of
// the app's pages (the sci-fi chrome — eyebrow, status pill, UTC clock — read
// as decoration, not information; armed state lives in the instrument band).
export const MissionHeader = ({ onShowSetup, onManageSub }: { onShowSetup?: () => void; onManageSub?: () => void }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    justifyContent="space-between"
    alignItems="flex-start"
    spacing={1.5}
    sx={(theme) => ({
      position: 'relative',
      pb: 2,
      mb: 2.5,
      // The same glowing gradient hairline that closes the locked landing's
      // banner — a whisper of the AI Connect identity tying the two surfaces
      // together, brightest under the title and fading toward the edges.
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: '1px',
        background: `linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.7)} 0%, ${alpha(
          theme.palette.primary.main,
          0.45
        )} 30%, transparent 85%)`
      }
    })}
  >
    <Box>
      <Typography variant="h3" component="h1" sx={pageTitleSx}>
        AI Connect control
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
        Connect an AI assistant to your live accounts and trade by chat — every order passes the server-enforced policy gate before it
        reaches your broker.
      </Typography>
    </Box>
    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
      {onManageSub && (
        <Button size="small" variant="text" onClick={onManageSub}>
          Manage subscription
        </Button>
      )}
      {onShowSetup && (
        <Button size="small" variant="text" onClick={onShowSetup}>
          Setup guide
        </Button>
      )}
    </Stack>
  </Stack>
);

// (ManageLink removed — it was unused and structurally broken: a <button>
// nested inside a raw <a> is invalid nested-interactive HTML, and the bare
// href forced a full client reload instead of a router navigation.)
